import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { NavigationRegions } from '@trustedshops/tswp-carrier-b2b-contracts';
import { RxJsBridge } from '@trustedshops/tswp-core-common-eventing-rxjs';
import { NavigationItem, NavigationRegionService, NavigationService, TOKENS } from '@trustedshops/tswp-core-ui';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { tertiaryNavigationRegion } from './tertiary-menu.navigation-region';
import { ViewTransitionDirectionService } from '../../services/view-transition-direction.service';
import { getNavigationItemIdentity } from '../../utils/navigation-item-identity.util';

@Component({
  selector: 'tertiary-menu-bar',
  templateUrl: './tertiary-menu-bar.component.html',
  styleUrls: ['./tertiary-menu-bar.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false,
})
export class TertiaryMenuBarComponent implements OnInit, OnDestroy {
  //#region Public Static Constants
  public static readonly selector: string = 'tswp-tertiary-menu-bar';
  //#endregion

  //#region Properties
  private _items: Observable<NavigationItem[]>;
  public get items(): Observable<NavigationItem[]> {
    return this._items;
  }

  private _activeItems: Observable<NavigationItem[]>;
  public get activeItems(): Observable<NavigationItem[]> {
    return this._activeItems;
  }

  private _previousActiveIndex: number | null = null;
  private _directionSubscription: Subscription | null = null;
  //#endregion

  //#region Ctor
  public constructor(
    @Inject(TOKENS.NavigationRegionService)
    private readonly _navigationRegionService: NavigationRegionService,

    @Inject(TOKENS.NavigationService)
    private readonly _navigationService: NavigationService,
    private readonly _viewTransitionDirectionService: ViewTransitionDirectionService) { }
  //#endregion

  //#region Public Methods
  public ngOnInit(): void {
    this._navigationRegionService.registerRegionConfig(tertiaryNavigationRegion);
    this._activeItems = this._navigationRegionService
      .getActiveItems(NavigationRegions.TertiaryMenu)
      .convertWith(RxJsBridge(BehaviorSubject))
      .pipe(filter(x => !!x));

    this._items = this._navigationRegionService
      .getActiveItems(NavigationRegions.SecondaryMenu)
      .convertWith(RxJsBridge(BehaviorSubject))
      .pipe(filter(x => !!x))
      .pipe(map(secondaryItems => secondaryItems
        .map(secondaryItem =>
          (secondaryItem.children ?? []).filter(secondaryChild =>
            secondaryChild.region === NavigationRegions.TertiaryMenu))
        .reduce((previous, current) => previous.concat(current), [] as NavigationItem[])));

    this._directionSubscription = combineLatest([this._items, this._activeItems])
      .subscribe(([items, activeItems]) => this.publishTertiaryNavigationDirection(items, activeItems));
  }
  //#endregion

  public ngOnDestroy(): void {
    this._directionSubscription?.unsubscribe();
    this._directionSubscription = null;
  }

  private publishTertiaryNavigationDirection(items: NavigationItem[], activeItems: NavigationItem[]): void {
    const sortedItems = [...(items || [])].sort((left, right) => (left?.order || 0) - (right?.order || 0));
    const currentActiveItem = (activeItems || [])[0];

    if (!currentActiveItem || sortedItems.length === 0) {
      this._previousActiveIndex = null;
      return;
    }

    const currentIdentity = getNavigationItemIdentity(currentActiveItem);
    const currentActiveIndex = sortedItems.findIndex(item =>
      getNavigationItemIdentity(item) === currentIdentity);
    if (currentActiveIndex < 0) {
      this._previousActiveIndex = null;
      return;
    }

    if (this._previousActiveIndex !== null && this._previousActiveIndex !== currentActiveIndex) {
      this._viewTransitionDirectionService
        .setDirection(currentActiveIndex > this._previousActiveIndex ? 'right' : 'left');
    }

    this._previousActiveIndex = currentActiveIndex;
  }

}
